import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { UserPlus, X, ShieldCheck } from 'lucide-react';

// Map department slug → roles users can be assigned within that department
const DEPARTMENT_ROLES: Record<string, { role: string; label: string }[]> = {
  sales: [
    { role: 'sales_agent', label: 'Sales Agent' },
    { role: 'sales_team_leader', label: 'Sales Team Leader' },
    { role: 'sales_manager', label: 'Sales Manager' },
  ],
  marketing: [
    { role: 'marketing_agent', label: 'Marketing Agent' },
    { role: 'marketing_team_leader', label: 'Marketing Team Leader' },
    { role: 'marketing_manager', label: 'Marketing Manager' },
  ],
  quality_control: [
    { role: 'quality_control_agent', label: 'QC Agent' },
    { role: 'quality_control_lead', label: 'QC Lead' },
  ],
  ad_operations: [
    { role: 'ad_operations_agent', label: 'Ad Ops Agent' },
    { role: 'ad_operations_lead', label: 'Ad Ops Lead' },
  ],
  customer_support: [
    { role: 'customer_support_agent', label: 'Customer Support' },
  ],
  it: [
    { role: 'it_staff', label: 'IT Staff' },
    { role: 'it_manager', label: 'IT Manager' },
  ],
  accounting: [{ role: 'accounting_staff', label: 'Accounting Staff' }],
  hr: [{ role: 'hr_staff', label: 'HR Staff' }],
};

const ROLE_BADGE_CONFIG: Record<string, { label: string; color: string }> = {
  user: { label: 'User', color: 'bg-muted text-muted-foreground' },
  agent: { label: 'Agent', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300' },
  agency_admin: { label: 'Agency Admin', color: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300' },
  developer: { label: 'Developer', color: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-300' },
  superadmin: { label: 'Super Admin', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300' },
  sales_agent: { label: 'Sales Agent', color: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300' },
  sales_team_leader: { label: 'Sales Team Leader', color: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300' },
  sales_manager: { label: 'Sales Manager', color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300' },
  marketing_agent: { label: 'Marketing Agent', color: 'bg-pink-100 text-pink-800 dark:bg-pink-900/30 dark:text-pink-300' },
  marketing_team_leader: { label: 'Marketing Team Leader', color: 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-300' },
  marketing_manager: { label: 'Marketing Manager', color: 'bg-fuchsia-100 text-fuchsia-800 dark:bg-fuchsia-900/30 dark:text-fuchsia-300' },
  quality_control_agent: { label: 'QC Agent', color: 'bg-violet-100 text-violet-800 dark:bg-violet-900/30 dark:text-violet-300' },
  quality_control_lead: { label: 'QC Lead', color: 'bg-violet-200 text-violet-900 dark:bg-violet-900/40 dark:text-violet-200' },
  ad_operations_agent: { label: 'Ad Ops', color: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300' },
  ad_operations_lead: { label: 'Ad Ops Lead', color: 'bg-sky-200 text-sky-900 dark:bg-sky-900/40 dark:text-sky-200' },
  customer_support_agent: { label: 'Support', color: 'bg-lime-100 text-lime-800 dark:bg-lime-900/30 dark:text-lime-300' },
  it_staff: { label: 'IT Staff', color: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/30 dark:text-cyan-300' },
  it_manager: { label: 'IT Manager', color: 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300' },
  accounting_staff: { label: 'Accounting', color: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300' },
  hr_staff: { label: 'HR Staff', color: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300' },
};

export { ROLE_BADGE_CONFIG };

interface AssignRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: any;
}

export function AssignRoleDialog({ open, onOpenChange, user }: AssignRoleDialogProps) {
  const queryClient = useQueryClient();
  const [selectedDeptId, setSelectedDeptId] = useState('');
  const [selectedRole, setSelectedRole] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');

  const { data: departments = [] } = useQuery({
    queryKey: ['internal-departments'],
    queryFn: async () => {
      const { data, error } = await supabase.from('internal_departments').select('*').order('name');
      if (error) throw error;
      return data || [];
    },
    enabled: open,
  });

  const selectedDept = useMemo(
    () => departments.find((d: any) => d.id === selectedDeptId),
    [departments, selectedDeptId],
  );

  const { data: teams = [] } = useQuery({
    queryKey: ['internal-teams-for-dept', selectedDeptId],
    queryFn: async () => {
      if (!selectedDeptId) return [];
      const { data, error } = await supabase.from('internal_teams').select('*').eq('department_id', selectedDeptId);
      if (error) throw error;
      return data || [];
    },
    enabled: !!selectedDeptId,
  });

  const availableRoles = useMemo(() => {
    if (!selectedDept) return [];
    return DEPARTMENT_ROLES[selectedDept.slug] || [];
  }, [selectedDept]);

  const existingRoles: string[] = user?.roles || [];
  const PUBLIC_ROLES = new Set(['user', 'agent', 'agency_admin', 'developer', 'superadmin']);
  const isInternalRole = (r: string) => !PUBLIC_ROLES.has(r);
  const userIsSuperadmin = existingRoles.includes('superadmin');
  const existingInternalRole = existingRoles.find(isInternalRole);
  const willReplaceInternal =
    !userIsSuperadmin &&
    !!existingInternalRole &&
    isInternalRole(selectedRole) &&
    existingInternalRole !== selectedRole;

  const assignMutation = useMutation({
    mutationFn: async () => {
      // 0) If replacing an internal role, remove the previous internal role + its team memberships first
      if (willReplaceInternal && existingInternalRole) {
        // Drop team memberships (department-bound)
        await supabase
          .from('internal_team_members')
          .delete()
          .eq('user_id', user.user_id);
        const { error: delErr } = await supabase
          .from('user_roles')
          .delete()
          .eq('user_id', user.user_id)
          .eq('role', existingInternalRole as any);
        if (delErr) throw delErr;
      }

      // 1) Insert role
      const { error } = await supabase.from('user_roles').insert({
        user_id: user.user_id,
        role: selectedRole as any,
      });
      if (error && !error.message.includes('duplicate')) throw error;

      // 2) Optionally assign to team
      if (selectedTeam && selectedTeam !== 'none') {
        const { error: teamErr } = await supabase.from('internal_team_members').insert({
          user_id: user.user_id,
          team_id: selectedTeam,
        });
        if (teamErr && !teamErr.message.includes('duplicate')) throw teamErr;
      }

      // 3) Auto-create internal_staff_permissions row with defaults
      if (selectedDept) {
        await supabase.from('internal_staff_permissions').upsert(
          {
            user_id: user.user_id,
            department_id: selectedDept.id,
            can_create_tasks: true,
            can_manage_prospects: selectedDept.slug === 'sales',
            can_manage_campaigns: selectedDept.slug === 'marketing',
          },
          { onConflict: 'user_id,department_id' as any },
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success(`Role "${ROLE_BADGE_CONFIG[selectedRole]?.label || selectedRole}" assigned.`);
      setSelectedDeptId('');
      setSelectedRole('');
      setSelectedTeam('');
      onOpenChange(false);
    },
    onError: (err: any) => {
      toast.error(err.message || 'Failed to assign role');
    },
  });

  const removeMutation = useMutation({
    mutationFn: async (role: string) => {
      const { error } = await supabase
        .from('user_roles')
        .delete()
        .eq('user_id', user.user_id)
        .eq('role', role as any);
      if (error) throw error;
    },
    onSuccess: (_, role) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      toast.success(`Role "${ROLE_BADGE_CONFIG[role]?.label || role}" removed.`);
    },
    onError: (err: any) => toast.error(err.message),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <UserPlus className="h-5 w-5 text-primary" /> Manage Roles — {user?.full_name || 'User'}
          </DialogTitle>
          <DialogDescription>Assign or remove department roles for this user.</DialogDescription>
        </DialogHeader>

        {/* Current roles */}
        <div>
          <div className="text-xs font-medium text-muted-foreground mb-2">Current Roles</div>
          <div className="flex flex-wrap gap-1.5">
            {existingRoles.map((r: string) => (
              <Badge key={r} className={`text-[10px] gap-1 ${ROLE_BADGE_CONFIG[r]?.color || ''}`}>
                {ROLE_BADGE_CONFIG[r]?.label || r}
                {r !== 'user' && r !== 'superadmin' && (
                  <button
                    onClick={() => removeMutation.mutate(r)}
                    className="ml-0.5 hover:text-red-600"
                    disabled={removeMutation.isPending}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </Badge>
            ))}
          </div>
        </div>

        {/* Assign new role */}
        <div className="space-y-3 pt-2">
          <div className="text-xs font-medium text-muted-foreground">Assign New Role</div>
          <Select value={selectedDeptId} onValueChange={(v) => { setSelectedDeptId(v); setSelectedRole(''); setSelectedTeam(''); }}>
            <SelectTrigger className="h-9">
              <SelectValue placeholder="Select Department" />
            </SelectTrigger>
            <SelectContent>
              {departments.map((d: any) => (
                <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {selectedDept && availableRoles.length > 0 && (
            <Select value={selectedRole} onValueChange={setSelectedRole}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Select Role" />
              </SelectTrigger>
              <SelectContent>
                {availableRoles
                  .filter((r) => !existingRoles.includes(r.role))
                  .map((r) => (
                    <SelectItem key={r.role} value={r.role}>{r.label}</SelectItem>
                  ))}
              </SelectContent>
            </Select>
          )}

          {selectedRole && teams.length > 0 && (
            <Select value={selectedTeam} onValueChange={setSelectedTeam}>
              <SelectTrigger className="h-9">
                <SelectValue placeholder="Assign to Team (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Team</SelectItem>
                {teams.map((t: any) => (
                  <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {willReplaceInternal && existingInternalRole && (
            <div className="p-3 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/30 text-xs space-y-1">
              <div className="font-medium text-amber-900 dark:text-amber-200">Replace existing internal role</div>
              <p className="text-amber-800 dark:text-amber-300">
                This user currently holds <strong>{ROLE_BADGE_CONFIG[existingInternalRole]?.label || existingInternalRole}</strong>.
                Only SuperAdmins can hold multiple roles. Continuing will <strong>remove</strong> the existing internal role and any team memberships, then assign the new role.
              </p>
            </div>
          )}

          {selectedRole && selectedDept && (
            <div className="p-3 rounded-lg bg-muted/50 border text-xs space-y-1">
              <div className="flex items-center gap-1 font-medium">
                <ShieldCheck className="h-3.5 w-3.5 text-primary" /> Preview
              </div>
              <p className="text-muted-foreground">
                User will get role <Badge variant="secondary" className="text-[10px] mx-1">{ROLE_BADGE_CONFIG[selectedRole]?.label || selectedRole}</Badge>
                in <strong>{selectedDept.name}</strong>
                {selectedTeam && selectedTeam !== 'none' && <> on team <strong>{teams.find((t: any) => t.id === selectedTeam)?.name}</strong></>}
                . Default permissions (create tasks{selectedDept.slug === 'sales' ? ', manage prospects' : ''}{selectedDept.slug === 'marketing' ? ', manage campaigns' : ''}) will be applied automatically.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button
            onClick={() => assignMutation.mutate()}
            disabled={!selectedRole || assignMutation.isPending}
          >
            {willReplaceInternal ? 'Replace & Assign' : 'Assign Role'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
